import React, { useState, useRef, useEffect } from 'react';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import {
  Camera,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  AlertCircle,
  ScanFace,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';

const MODELS_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.12/model';

const FaceRegistrationPage = () => {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectionIntervalRef = useRef(null);
  
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [faceDetected, setFaceDetected] = useState(false);
  const [capturedDescriptor, setCapturedDescriptor] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [saving, setSaving] = useState(false);
  const [faceapi, setFaceapi] = useState(null);
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    loadFaceApiModels();
    checkRegistration();
    return () => stopCamera();
  }, []);

  const loadFaceApiModels = async () => {
    try {
      const faceapiModule = await import('@vladmandic/face-api');
      setFaceapi(faceapiModule);
      setLoadingProgress(10);

      // Force webgl or cpu backend; skip wasm (binary not bundled, causes init failure)
      try {
        await faceapiModule.tf.setBackend('webgl');
        await faceapiModule.tf.ready();
        // Verify webgl actually initialized (can silent-fail on some devices/browsers)
        faceapiModule.tf.tidy(() => faceapiModule.tf.zeros([1]).dataSync());
      } catch (backendError) {
        await faceapiModule.tf.setBackend('cpu');
        await faceapiModule.tf.ready();
      }

      await faceapiModule.nets.tinyFaceDetector.loadFromUri(MODELS_URL);
      setLoadingProgress(40);
      await faceapiModule.nets.faceLandmark68Net.loadFromUri(MODELS_URL);
      setLoadingProgress(70);
      await faceapiModule.nets.faceRecognitionNet.loadFromUri(MODELS_URL);
      setLoadingProgress(100);
      
      setModelsLoaded(true);
      setModelsLoading(false);
    } catch (error) {
      console.error('Failed to load face-api models:', error);
      setModelsLoading(false);
      toast.error('Gagal memuat model pengenalan wajah');
    }
  };

  const checkRegistration = async () => {
    try {
      const response = await api.get('/face/check');
      setIsRegistered(response.data.registered);
    } catch (error) {
      console.error('Failed to check registration:', error);
    }
  };

  const startCamera = async () => {
    setCameraLoading(true);
    setCameraError(null);
    setFaceDetected(false);
    
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError('Browser tidak mendukung akses kamera.');
      setCameraLoading(false);
      return;
    }

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user', 
          width: { ideal: 480 }, 
          height: { ideal: 640 }
        },
        audio: false,
      });
      
      streamRef.current = mediaStream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = async () => {
          try {
            await videoRef.current.play();
            setCameraActive(true);
            setCameraLoading(false);
            startFaceDetection();
          } catch (playError) {
            setCameraError('Gagal memutar video: ' + playError.message);
            setCameraLoading(false);
          }
        };
      }
    } catch (error) {
      console.error('Camera error:', error);
      setCameraLoading(false);
      let errMsg = 'Gagal mengakses kamera: ' + error.name;
      if (error.name === 'NotAllowedError') {
        errMsg = 'Izin kamera ditolak. Berikan izin di pengaturan browser.';
      }
      setCameraError(errMsg);
      toast.error(errMsg);
    }
  };

  const stopCamera = () => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setFaceDetected(false);
  };

  const startFaceDetection = () => {
    if (!faceapi || !videoRef.current) return;

    detectionIntervalRef.current = setInterval(async () => {
      if (!videoRef.current || videoRef.current.paused || videoRef.current.ended) return;

      try {
        const detections = await faceapi
          .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 }))
          .withFaceLandmarks()
          .withFaceDescriptor();

        setFaceDetected(!!detections);
        
        if (canvasRef.current && videoRef.current.videoWidth > 0) {
          const displaySize = { width: videoRef.current.videoWidth, height: videoRef.current.videoHeight };
          faceapi.matchDimensions(canvasRef.current, displaySize);
          const ctx = canvasRef.current.getContext('2d');
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          
          if (detections) {
            const resizedDetections = faceapi.resizeResults(detections, displaySize);
            faceapi.draw.drawDetections(canvasRef.current, resizedDetections);
          }
        }
      } catch (error) {
        console.error('Face detection error:', error);
      }
    }, 300);
  };

  const captureAndExtract = async () => {
    if (!faceapi || !videoRef.current) {
      toast.error('Kamera atau model belum siap');
      return;
    }

    setCapturing(true);
    
    try {
      const detections = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.5 }))
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detections) {
        toast.error('Wajah tidak terdeteksi. Pastikan wajah terlihat jelas.');
        setCapturing(false);
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
      const photoData = canvas.toDataURL('image/jpeg', 0.8);

      setCapturedDescriptor(Array.from(detections.descriptor));
      setCapturedPhoto(photoData);
      stopCamera();
      toast.success('Wajah berhasil diambil!');
    } catch (error) {
      console.error('Capture error:', error);
      toast.error('Gagal mengambil data wajah: ' + error.message);
    } finally {
      setCapturing(false);
    }
  };

  const saveFaceDescriptor = async () => {
    if (!capturedDescriptor) return;

    setSaving(true);
    try {
      await api.post('/face/register', {
        face_descriptor: capturedDescriptor,
        foto_url: capturedPhoto,
      });
      
      toast.success('Wajah berhasil didaftarkan!');
      setIsRegistered(true);
      setCapturedDescriptor(null);
      setCapturedPhoto(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan data wajah');
    } finally {
      setSaving(false);
    }
  };

  const resetCapture = () => {
    setCapturedDescriptor(null);
    setCapturedPhoto(null);
    setFaceDetected(false);
    setCameraError(null);
  };

  if (!user?.employee_id) {
    return (
      <div className="space-y-6" data-testid="face-registration-page">
        <h1 className="text-2xl font-bold">Registrasi Wajah</h1>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Akun belum terhubung dengan data karyawan. Hubungi HR.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="face-registration-page">
      <div>
        <h1 className="text-2xl font-bold">Registrasi Wajah</h1>
        <p className="text-muted-foreground text-sm mt-1">Daftarkan wajah untuk verifikasi absensi</p>
      </div>

      <Card className={isRegistered ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isRegistered ? 'bg-green-100' : 'bg-orange-100'}`}>
              {isRegistered ? <Shield className="w-7 h-7 text-green-600" /> : <ScanFace className="w-7 h-7 text-orange-500" />}
            </div>
            <div>
              <h3 className="font-semibold text-lg">{isRegistered ? 'Wajah Terdaftar' : 'Wajah Belum Terdaftar'}</h3>
              <p className="text-sm text-muted-foreground">
                {isRegistered ? 'Anda dapat menggunakan verifikasi wajah saat absensi' : 'Anda WAJIB mendaftarkan wajah sebelum bisa absen'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {modelsLoading && (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <span>Memuat model pengenalan wajah...</span>
              </div>
              <Progress value={loadingProgress} className="h-2" />
            </div>
          </CardContent>
        </Card>
      )}

      {cameraError && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{cameraError}</AlertDescription>
        </Alert>
      )}

      {modelsLoaded && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{capturedDescriptor ? 'Konfirmasi Wajah' : 'Ambil Foto Wajah'}</CardTitle>
            <CardDescription>
              {capturedDescriptor ? 'Data wajah berhasil diambil. Klik simpan untuk mendaftarkan.' : 'Posisikan wajah di tengah frame'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative w-full max-w-[280px] mx-auto" style={{ aspectRatio: '3/4' }}>
              <div className="absolute inset-0 bg-slate-900 rounded-lg overflow-hidden">
                {!cameraActive && !capturedPhoto && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                    <Camera className="w-12 h-12 mb-4 opacity-50" />
                    <Button onClick={startCamera} disabled={cameraLoading}>
                      {cameraLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Membuka...</>
                      ) : (
                        'Buka Kamera'
                      )}
                    </Button>
                  </div>
                )}

                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ display: cameraActive && !capturedPhoto ? 'block' : 'none' }}
                  className="w-full h-full object-cover"
                />
                
                {cameraActive && !capturedPhoto && (
                  <>
                    <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full pointer-events-none" />
                    <div className="absolute top-3 left-3">
                      {faceDetected ? (
                        <div className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white rounded-full text-xs font-medium">
                          <CheckCircle className="w-3 h-3" /> Wajah Terdeteksi
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 px-2 py-1 bg-yellow-500 text-white rounded-full text-xs font-medium animate-pulse">
                          <Loader2 className="w-3 h-3 animate-spin" /> Mencari Wajah...
                        </div>
                      )}
                    </div>
                    <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
                      <Button size="sm" onClick={captureAndExtract} disabled={!faceDetected || capturing}>
                        {capturing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <><Camera className="w-4 h-4 mr-1" /> Ambil</>
                        )}
                      </Button>
                      <Button size="sm" variant="secondary" onClick={stopCamera}>Batal</Button>
                    </div>
                  </>
                )}

                {capturedPhoto && (
                  <img src={capturedPhoto} alt="Captured" className="w-full h-full object-cover" />
                )}
              </div>
            </div>

            {capturedDescriptor && (
              <div className="flex gap-3 max-w-[280px] mx-auto">
                <Button onClick={saveFaceDescriptor} disabled={saving} className="flex-1">
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Menyimpan...</>
                  ) : (
                    <><Shield className="w-4 h-4 mr-2" />Simpan Wajah</>
                  )}
                </Button>
                <Button variant="outline" onClick={resetCapture}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            )}

            <Alert className="max-w-[280px] mx-auto">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside text-xs space-y-1 mt-1">
                  <li>Pastikan pencahayaan cukup</li>
                  <li>Wajah menghadap kamera</li>
                  <li>Lepas aksesoris wajah</li>
                </ul>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}

      {isRegistered && modelsLoaded && !cameraActive && !capturedDescriptor && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Perbarui Data Wajah</p>
                <p className="text-sm text-muted-foreground">Jika verifikasi sering gagal</p>
              </div>
              <Button variant="outline" onClick={startCamera}>
                <RefreshCw className="w-4 h-4 mr-2" />Daftar Ulang
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FaceRegistrationPage;
